import { ApolloClient, ApolloLink, InMemoryCache, HttpLink } from '@apollo/client'
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { ErrorLink } from '@apollo/client/link/error'
import { SetContextLink } from '@apollo/client/link/context'
import { Observable } from '@apollo/client/utilities'
import * as Sentry from '@sentry/react'
import { getEnv } from '@/lib/env'
import { cerrarSesionLocal, renovarOCerrarSesion } from '@/lib/auth/sesion'
import { tokenStore } from '@/lib/auth/tokenStore'
import { reportarErroresGraphQL } from './reporteDeErrores'

/**
 * Resuelve la URL del gateway GraphQL desde la variable de entorno.
 * Debe estar configurada en Rancher para cada entorno
 * (development → gatedev, staging → gatebeta, production → gate)
 * y en `.env.local` para desarrollo local.
 */
function resolveGatewayUrl(): string {
  const url = getEnv('VITE_GATEWAY_URL')
  if (url) return url
  throw new Error(
    'VITE_GATEWAY_URL no configurada. Configura la variable en Rancher o en .env.local.',
  )
}

const GATEWAY_URL = resolveGatewayUrl()

/**
 * authLink: inyecta `Authorization: Bearer <token>` si hay un token en memoria.
 * Skipea cuando la operación marca `context: { skipAuth: true }` (lo usaría una
 * operación pública si existiera; refreshToken usa fetch directo así que no pasa
 * por este link).
 */
const authLink = new SetContextLink((prevContext) => {
  const ctx = prevContext as { skipAuth?: boolean; headers?: Record<string, string> }
  if (ctx.skipAuth) return prevContext
  const token = tokenStore.get()
  return {
    headers: {
      ...ctx.headers,
      ...(token && { authorization: `Bearer ${token}` }),
    },
  }
})

function isUnauthorized(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const e = error as {
    statusCode?: number
    status?: number
    cause?: { statusCode?: number; status?: number }
  }
  return (
    e.statusCode === 401 ||
    e.status === 401 ||
    e.cause?.statusCode === 401 ||
    e.cause?.status === 401
  )
}

/**
 * errorLink — la cara GraphQL del módulo canónico de sesión. La política vive en
 * `@/lib/auth/sesion` (los tres desenlaces); aquí solo se DETECTA el fallo de
 * sesión y se reenvía la operación:
 * - `extensions.code === 'UNAUTHENTICATED'` (o HTTP 401) → renueva y reintenta
 *   UNA vez. `FORBIDDEN` **no**: es autorización y reloguear no la arregla
 *   (raíz §5.6).
 * - Renovación rechazada por el servidor → `cerrarSesionLocal` (limpia + /login)
 *   y el error llega a la operación como `ApiUnauthorizedError`.
 * - Renovación caída por RED (5xx, timeout, sin respuesta) → `ErrorDeRed`: la
 *   sesión NO se toca y la UI lo pinta como fallo de conexión. Antes esto
 *   desLOGUEABA (defecto #7).
 * - GraphQL errors normales → los de negocio (`FORBIDDEN`, `NOT_FOUND`…) quedan
 *   como miga en Sentry y el resto se reporta como error; todos se propagan.
 */
export const errorLink = new ErrorLink(({ error, operation, forward }) => {
  if (CombinedGraphQLErrors.is(error)) {
    const hasUnauthenticated = error.errors.some(
      (err) => err.extensions?.['code'] === 'UNAUTHENTICATED',
    )

    if (!hasUnauthenticated) {
      reportarErroresGraphQL(error.errors, operation.operationName)
      return
    }

    // Fall through to the refresh + retry logic below.
  } else if (!isUnauthorized(error)) {
    Sentry.captureException(error, {
      tags: { operation: operation.operationName ?? 'unknown' },
    })
    return
  }

  // 401 en logout → la sesión ya estaba muerta; limpia y redirige sin retry.
  if (operation.operationName === 'logout') {
    cerrarSesionLocal()
    return
  }

  // 401 en cualquier otra operación → renovar + reintentar una sola vez.
  return new Observable((subscriber) => {
    renovarOCerrarSesion()
      .then(() => {
        // Apollo aplica el authLink automáticamente al reentrar; el token nuevo
        // ya está en el tokenStore (lo guarda la renovación), así que `forward`
        // lo recoge.
        forward(operation).subscribe({
          next: (value) => subscriber.next(value),
          error: (err) => subscriber.error(err),
          complete: () => subscriber.complete(),
        })
      })
      // `ApiUnauthorizedError` (la sesión ya se cerró y se navegó al login) o
      // `ErrorDeRed` (sesión intacta, fallo de conexión): en los dos casos el
      // error viaja a la operación original y NADIE más redirige.
      .catch((err: unknown) => subscriber.error(err))
  })
})

const httpLink = new HttpLink({
  uri: GATEWAY_URL,
  credentials: 'include',
})

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      Me: { keyFields: ['id'] },
      Organization: { keyFields: ['id'] },
      Business: { keyFields: ['id'] },
      Invoice: { keyFields: ['id'] },
      Service: { keyFields: ['id'] },
      Role: { keyFields: ['id'] },
    },
  }),
})
